/* eslint-disable import/no-dynamic-require, padded-blocks, prefer-template */
/* global process */

import apiService from '../services/api/ApiService';
import apiTools from '../services/api/ApiTools';
import { responseCategories } from '../services/api/ApiConstants';
import cacheManager, { cacheContexts } from './resources/cacheManager';
import localCache from '../services/LocalCache';
import navigationService from '../services/NavigationService';
import questionModeService from '../services/QuestionModeService';
import responseService from '../services/ResponseService';
import { reportError } from '../services/ErrorReporting';
import stateRestorationService from '../services/StateRestoration';
import { t } from '../services/i18nService';
import timeService from '../services/TimeService';
import alertService from '../services/AlertService';
import utilService from '../services/UtilService';
import aclService from '../services/AclService';

import assessmentActions from '../actions/AssessmentActions';
import correctResponsesActions from '../actions/CorrectResponsesActions';
import responseActions from '../actions/ResponseActions';
import resultActions from '../actions/ResultActions';
import settingsActions from '../actions/SettingsActions';
import sessionActions from '../actions/SessionActions';
import { setIdVerificationStatus } from '../actions/IdVerificationActions';
import deliveryDetailsActions from '../actions/DeliveryDetailsActions';
import readinessCheckActions from '../actions/ReadinessCheckActions';

import assessmentReader from '../store/readHelpers/AssessmentReader';
import responseReader from '../store/readHelpers/ResponseReader';
import sessionReader from '../store/readHelpers/SessionReader';
import settingsReader from '../store/readHelpers/SettingsReader';
import storeManager from '../store/StoreManager';

import awsIOTService from '../services/AWSIOTService';
import chromeAppService from '../services/ChromeAppService';
import globalsService from '../services/GlobalsService';
import viewReadyTracker from '../services/ViewReadyTracker';
import backgroundAudioService from '../services/BackgroundAudioService';
import pendingAssessmentsActions from '../actions/PendingAssessmentsActions';
import parentTestService from '../services/ParentTestService';
import testStatusConstants from '../constants/TestStatusConstants';
import dataCleanUpService from '../services/DataCleanUpService';
import ServiceWorkerService from '../services/ServiceWorkerService';
import messagesService from '../services/MessagesService';
import resourceCacheService from './resources/resourceCacheService';
import deliverService from '../services/DeliveringService';
import idleWarningService from '../services/IdleWarningService';
import remoteOralExamService from '../services/RemoteOralExamService';
import stateCacheService from '../services/StateCache';
import readinessCheckService from '../services/ReadinessCheckService';
import assessmentService from '../services/AssessmentService';
import questionParsingQueue from '../services/QuestionParsingQueue';
import * as hardwareRequirements from '../services/HardwareRequirements';
import candidateProgressConstants from '../constants/CandidateProgressConstants';
import loginService from '../services/LoginService';
import DataLoadingService from '../services/DataLoadingService';
import reloginService from '../services/ReloginService';
import hardwareDetectionService from '../services/HardwareDetectionService';

const liveDataLoader = {
    async startTest() {
        const { resumed, sessionInfo } = await apiService.startTest(true);
        sessionInfo.startTime = utilService.getIsoDateStamp();
        storeManager.dispatch(sessionActions.setSessionInfo(sessionInfo));
        storeManager.dispatch(sessionActions.setResumed(resumed));
        if (sessionInfo.idVerificationStatus) {
            storeManager.dispatch(setIdVerificationStatus(sessionInfo.idVerificationStatus));
        }
    },

    async loadQuestionSet() {
        const assessment = await apiService.fetchQuestionSet();
        storeManager.dispatch(assessmentActions.assessmentLoadSuccess(assessment));
    },

    async loadQuestion() {
        await this.loadSettings();
        const question = await apiService.fetchQuestion();
        const mockedAssessment = questionModeService.generateAssessmentForSingleQuestion(question);
        storeManager.dispatch(assessmentActions.assessmentLoadSuccess(mockedAssessment));
        await assessmentActions.parseQuestion(
            question.contentRevisionId,
            assessmentReader.getSupportedLanguages()
        );

        if (sessionReader.isContextViewGradingQuestionDelivery() || sessionReader.isContextViewOwnDelivery()) {
            try {
                await responseService.fetchUserResponseForOneQuestion(question.contentRevisionId);
            } catch (error) {
                // Silently ignore.
            }
        }

        if (DataLoadingService.shouldLoadCorrectResponses()) {
            await this.loadCorrectResponses();
        }
    },

    async loadSection() {
        await this.loadSettings();
        const section = await apiService.fetchSection();
        const mockedAssessment = questionModeService.generateAssessmentForSingleSection(section);
        storeManager.dispatch(assessmentActions.assessmentLoadSuccess(mockedAssessment));

        if (DataLoadingService.shouldLoadCorrectResponses()) {
            await this.loadCorrectResponses();
        }
    },

    async loadCorrectResponses() {
        const params = {};

        if (sessionReader.isAnswerKeysMode()) {
            params.presentation = 'pbKeys';
        }

        return apiService
            .fetchCorrectResponses(params)
            .then((correctResponses) => {
                storeManager.dispatch(
                    correctResponsesActions.setCorrectResponses(correctResponses)
                );
            })
            .catch((error) => {
                if (error.errorType === responseCategories.FORBIDDEN) {
                    return window.console.log('Correct responses not available to this user');
                }
                return window.console.log('Correct responses could not be loaded. Error received:', error);
            });
    },

    async loadCorrectResponsesForReadinessCheck(parentAssessmentRunId, readinessCheckContentRevisionId) {
        return apiService
            .fetchCorrectResponsesForReadinessCheck(parentAssessmentRunId, readinessCheckContentRevisionId)
            .then((correctResponses) => {
                storeManager.dispatch(
                    correctResponsesActions.setCorrectResponses(correctResponses)
                );
            })
            .catch((error) => {
                if (error.errorType === responseCategories.FORBIDDEN) {
                    return window.console.log('Correct responses not available to this user');
                }
                return window.console.log('Correct responses could not be loaded. Error received:', error);
            });
    },

    async loadDeliveryDetails() {
        try {
            const details = await apiService.fetchDeliveryDetails();
            storeManager.dispatch(
                deliveryDetailsActions.deliveryDetailsLoadSuccess(details)
            );
        } catch (error) {
            storeManager.dispatch(
                deliveryDetailsActions.deliveryDetailsLoadFailure(error)
            );
            throw error;
        }
    },

    async loadParentTestQuestionSet() {
        await parentTestService.loadParentTestQuestionSet();
    },

    async loadParentTestChildTests() {
        await parentTestService.loadParentTestChildTests();
    },

    async loadSettings() {
        try {
            const settings = await apiService.fetchSettings();
            const marketplaceProperties = window.marketplaceProperties || {};
            const allSettings = Object.assign({ marketplaceProperties }, settings);

            storeManager.dispatch(
                settingsActions.settingsLoadSuccess(allSettings),
            );
        } catch (error) {
            storeManager.dispatch(
                settingsActions.settingsLoadFailed(error),
            );
            throw error;
        }
    },

    async loadSavedResponsesForExam() {
        await stateRestorationService.loadSavedResponsesForExam();
    },

    async loadSavedResponsesForDelivery() {
        try {
            const responses = await apiService.fetchUserResponses();

            if (!responseService.isResponseProcessingComplete(responses)) {
                storeManager.dispatch(sessionActions.setCriticalErrorOccurred());
                navigationService.navigateToResponseNotProcessedPage();
                window.console.error('Response has not completed processing');

                return;
            }

            // Set the response state.
            const parsedResponses = stateRestorationService.parseItemResultXMLsAsMap(responses.questions);
            storeManager.dispatch(
                responseActions.setResponseState(parsedResponses),
            );

            // Set the result state.
            const results = responseReader.getResultsFromResponses(responses.questions);
            storeManager.dispatch(
                resultActions.setResultState({
                    questions: results,
                    metaData: responses.metaData,
                })
            );
        } catch (error) {
            window.console.error(error);
        }
    },

    async loadSketchCodesForQuestions() {
        const isContextPreview = sessionReader.isContextPreview() || sessionReader.isContextViewGradingQuestionPreview();
        if (!settingsReader.areAttachedSketchesEnabled() || isContextPreview) {
            return;
        }

        const sketchCodes = await apiService.fetchSketchCodesForQuestions();
        storeManager.dispatch(
            assessmentActions.assessmentSketchCodesLoaded(sketchCodes)
        );
    },

    async loadFeedbackForQuestions() {
        if (!(
            settingsReader.isFeedbackEnabledInMarketplace()
            && settingsReader.isReceiptModeActive()
            && (
                settingsReader.isFeedbackForAlternativeEnabled()
                || settingsReader.isFeedbackForQuestionEnabled()
                || settingsReader.isInteractionFeedbackEnabled()
            )
        )) {
            return;
        }
        const feedbacks = await apiService.fetchFeedbackForQuestions();
        storeManager.dispatch(
            assessmentActions.assessmentFeedbackForQuestionsLoaded(feedbacks)
        );
    },

    async loadInitialDataForExam() {
        await this.startTest();
        await this.loadSettings();

        loginService.handleStudentEviction(sessionReader.getBlockageReason());

        if (!sessionReader.passesLockDownRequirements()) {
            return false;
        }

        if (sessionReader.hasReadinessCheck()) {
            await this.loadReadinessCheckData(sessionReader.getAssessmentRunId(), sessionReader.getReadinessCheckContentRevisionId());
        }

        if (sessionReader.isParentTest()) {
            await this.loadParentTestChildTests();
            await this.loadParentTestQuestionSet();
            await this.loadChildTestsData();

            await dataCleanUpService.deleteAllDataFromOtherAssessments(
                parentTestService.getAllPendingAssessmentRunIds()
            );

            if (ServiceWorkerService.browserSupportsServiceWorker()) {
                try {
                    await ServiceWorkerService.notifyWhenServiceWorkerIsActive();
                } catch (e) {
                    console.warn('Continuing without SW');
                }
            }
            resourceCacheService.cacheParentChildResourcesInBackground();

            await parentTestService.startParentServices();
            return true;
        }

        if (sessionReader.hasReadinessCheck()) {
            const readinessCheckContentRevisionId = sessionReader.getReadinessCheckContentRevisionId();
            await resourceCacheService.cacheReadinessCheckResources(readinessCheckContentRevisionId);
        }

        await dataCleanUpService.deleteAllDataFromOtherAssessments([sessionReader.getAssessmentRunId()]);

        if (sessionReader.isRemoteOralExam()) {
            await this.loadRemoteOralExamData();

            const { remoteOralExam: { sessionInfo } } = storeManager.getState();
            if (remoteOralExamService.isInProgress(sessionInfo)) {
                await this.loadQuestionSet();
            } else {
                assessmentService.populateDummyQuestionSet();
            }
        } else {
            await this.loadQuestionSet();
        }

        if (sessionReader.isResumed()) {
            window.console.log('Resume normal test');
            const promises = [
                stateRestorationService.restoreSavedQuestionFlags(),
                messagesService.fetchCompareAndAlertMessages(true),
            ];

            if (assessmentReader.isAssessmentReady()) {
                promises.push(this.loadSavedResponsesForExam());
            }

            await Promise.all(promises);
        }

        if (settingsReader.shouldCacheResourcesBeforeTestStart(storeManager.getState().settings)) {
            await cacheManager.cacheQuestionSetMedia(assessmentReader.getAssessment(), cacheContexts.TEST, false);
        }

        return true;
    },

    async loadChildTestsData() {
        const state = storeManager.getState();
        const { parentTest } = state;
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const promises = [];

        for (const childTest of parentTest.childTests) {
            if (childTest.status !== testStatusConstants.FINISHED) {
                if (deliverService.hasClientSideOfflineDeliveryInStateCache(childTest.assessmentRunId)) {
                    deliverService.handleClientSideOfflineDeliveryFromStateCache(childTest.assessmentRunId);
                } else {
                    promises.push(this.loadChildTestData(assessmentRunId, childTest.assessmentRunId, false));
                }
            }
        }
        return Promise.all(promises);
    },

    async loadReadinessCheckData(assessmentRunId, contentRevisionId) {
        if (stateCacheService.getStateCacheInstance().isReadinessCheckComplete(contentRevisionId)) {
            storeManager.dispatch(
                readinessCheckActions.setReadinessCheckComplete(true)
            );
            return Promise.resolve();
        }
        const promises = [];
        promises.push(this.loadChildTestData(assessmentRunId, contentRevisionId, true));
        promises.push(this.loadCorrectResponsesForReadinessCheck(assessmentRunId, contentRevisionId));
        return Promise.all(promises);
    },

    async loadChildTestData(parentAssessmentRunId, identifier, isReadinessCheck) {
        // Load question set for child test
        try {
            const assessment = await apiService.fetchChildQuestionSet(parentAssessmentRunId, identifier, isReadinessCheck);
            storeManager.dispatch(
                pendingAssessmentsActions.childAssessmentItemLoadSuccess(identifier, assessment),
            );
        } catch (e) {
            storeManager.dispatch(
                pendingAssessmentsActions.childAssessmentItemLoadFailed(identifier, e)
            );
        }

        // Load settings for child test
        try {
            const settings = await apiService.fetchChildSettings(parentAssessmentRunId, identifier, isReadinessCheck);

            const values = isReadinessCheck
                ? { ...settings.values, show_correct_answers: false }
                : settings.values;
            storeManager.dispatch(
                pendingAssessmentsActions.childTestSettingsLoadSuccess(identifier, { ...settings, values }),
            );
        } catch (e) {
            storeManager.dispatch(
                pendingAssessmentsActions.childTestSettingsLoadFailed(identifier, e)
            );
        }
    },

    async loadInitialDataForPreview() {
        await this.loadSettings();
        await this.loadQuestionSet();

        if (!(
            (sessionReader.isContextPreviewAssessmentRun()
                && !sessionReader.getPrintParam(sessionReader.printParamNames.SHOW_PREVIEW_WITH_FEEDBACK, false))
            && sessionReader.isViewMediaScreen()
        )) {
            // previewing from authoring or from Print Manager or in feedback-preview, needs correct answers
            await this.loadCorrectResponses();
        }

        await awsIOTService.loadSignedWebSocketUrl();
    },

    async loadInitialDataForDelivery() {
        await this.loadSettings();
        await this.loadQuestionSet();
        await Promise.all([
            this.loadSavedResponsesForDelivery(),
            this.loadSketchCodesForQuestions(),
        ]);

        await this.loadDeliveryDetails();
        if (sessionReader.isContextViewOwnDelivery()) {
            await this.loadFeedbackForQuestions();
        }

        if (!aclService.canSeeCorrectResponsesInDelivery()) {
            return;
        }
        await this.loadCorrectResponses();
    },

    async loadRemoteOralExamData() {
        const promises = [];
        promises.push(remoteOralExamService.getSessionInfo());

        // Force to use WebRTC test call if recording is enabled.
        if (sessionReader.isZoomHardwareAccessCheckEnabled()) {
            promises.push(remoteOralExamService.getZoomTestCallDetail());
        }

        return Promise.all(promises);
    },

    async loadDataForContext() {
        if (sessionReader.isDataSetTypeQuestion()) {
            return this.loadQuestion();
        }
        if (sessionReader.isDataSetTypeSection()) {
            return this.loadSection();
        }
        if (sessionReader.isContextExam()) {
            return this.loadInitialDataForExam();
        }
        if (sessionReader.isContextViewOwnDelivery()) {
            await this.loadInitialDataForDelivery(true);
            return stateRestorationService.restoreSavedQuestionFlags();
        }
        if (sessionReader.isContextViewDelivery() || sessionReader.isContextViewDeliveryForAnnotationPdf()) {
            return this.loadInitialDataForDelivery();
        }
        return this.loadInitialDataForPreview();
    },

    parseQuestions() {
        const questionIds = storeManager.getState().toc?.itemsOrderAllView?.map(item => item.contentRevisionId) || [];
        questionParsingQueue.queueAndStart(questionIds);
    },

    initServices() {
        const state = storeManager.getState();
        if (state.currentlyShown.routeType === 'error') {
            return;
        }

        if (hardwareDetectionService.isAdditionalScreenDetectionRequired()) {
            hardwareDetectionService.addAdditionalScreenDetector();
        }

        if (sessionReader.isModeSection() && assessmentReader.hasBackgroundAudioResource() && !sessionReader.isViewMediaPrintOrPdf()) {
            backgroundAudioService.playAudioForNonChildTest();
        }

        if (!sessionReader.isViewMediaScreen() || sessionReader.isModeQuestion() || sessionReader.isModeSection()) {
            return;
        }

        if (awsIOTService.allowIOTConnection()) {
            awsIOTService.setupClient();
        }

        if (sessionReader.isContextPreview() || sessionReader.isDemoExam() || sessionReader.isPublicExam()) {
            localCache.clearNonSpecificUserCache();
        }

        if (sessionReader.isContextExam() && !sessionReader.isResumed()) {
            localCache.getLocalCacheInstance().removeAll();
        }

        this.parseQuestions();
        apiTools.triggerPreloadingStatusInHeader(true);

        if (!localCache.isLocalStorageAvailable()) {
            alertService.showAlert(t('session.localStorageUnavailable'));
        }

        if (sessionReader.userHasChromeKiosk()) {
            chromeAppService.sendTestInfoToApp();
        }

        if (sessionReader.isContextExam()) {
            timeService.start(sessionReader.getAssessmentRunId());
            dataCleanUpService.assureAssessmentDataDeletionOnUnload();
            this.reportInitialStateToMonitor();
        }

        if (sessionReader.isContextExam() && settingsReader.isIdleStateWarningEnabled() && !sessionReader.isParentTest()) {
            idleWarningService.initialize();
        }

        if (
            !sessionReader.isRequiringPasswordForResume() &&
            !sessionReader.supportsTestOverview() &&
            !sessionReader.isViewMediaPrintOrPdf() &&
            assessmentReader.hasBackgroundAudioResource()
        ) {
            backgroundAudioService.playAudioForNonChildTest();
        }
    },

    reportInitialStateToMonitor() {
        if (sessionReader.isPublicExam()) {
            return;
        }

        if (sessionReader.hasReadinessCheck()) {
            readinessCheckService.reportInitialReadinessCheckStateToMonitor();
            return;
        }

        if (hardwareRequirements.needsHardware()) {
            apiService.notifyClientEvent(candidateProgressConstants.READY, '', true);
        }
    },

    async loadInitialData() {
        if (sessionReader.isContextEmpty()) {
            return;
        }
        try {
            // run the startup requests in parallel
            await this.loadDataForContext();
            this.initServices();

            storeManager.dispatch(sessionActions.setAppReady(true));
            globalsService.setTestPlayerReady();
            if (reloginService.isReloginRedirectAllowed()) {
                const result = reloginService.navigateToResumedQuestionIfStandaloneTestExposable();
                if (result) {
                    return;
                }
            }
            navigationService.navigateToStartingPoint();
        } catch (error) {
            viewReadyTracker.logError();
            reportError(error);
        }
    },
};

export default liveDataLoader;
